
import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrandConfig, BrandContextType } from '@/types/brand';
import { SecureStorage, BrandConfigSchema } from '@/utils/secureStorage';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { toast } from 'sonner';
import { z } from 'zod';

const BrandContext = createContext<BrandContextType | undefined>(undefined);

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};

export const BrandProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentBrand, setCurrentBrand] = useState<BrandConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { currentUser } = useUser();

  // Migration function to move localStorage brands to database
  const migrateBrandsToDatabase = async () => {
    try {
      const localBrands = SecureStorage.getItem('aicoach-brands', z.array(BrandConfigSchema), []) as BrandConfig[];
      if (localBrands.length === 0) return;

      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      // Check if migration already happened
      const migrationKey = `brands-migrated-${user.user.id}`;
      if (localStorage.getItem(migrationKey)) return;

      // Insert each brand into the database
      for (const brand of localBrands) {
        await supabase
          .from('brands')
          .upsert({
            id: brand.id,
            name: brand.name,
            slug: brand.name.toLowerCase().replace(/\s+/g, '-'),
            config: brand as any,
            user_id: user.user.id,
            organization_id: null // Will be null for individual users
          });
      }

      // Mark migration as complete
      localStorage.setItem(migrationKey, 'true');
      
      // Optionally clear localStorage brands after successful migration
      SecureStorage.removeItem('aicoach-brands');
    } catch (error) {
      console.error('Error migrating brands to database:', error);
    }
  };

  // Initialize and run migration on mount
  useEffect(() => {
    migrateBrandsToDatabase();
  }, []);

  const saveBrand = async (brand: BrandConfig): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Ensure brand has proper slug
      const brandWithSlug = {
        ...brand,
        slug: brand.slug || brand.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      };
      
      // Add timeout for getUser call
      const getUserWithTimeout = () => {
        return Promise.race([
          supabase.auth.getUser(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('GetUser timeout')), 2000)
          )
        ]);
      };
      
      let user, userError;
      try {
        const result = await getUserWithTimeout() as any;
        user = result.data?.user;
        userError = result.error;
      } catch (timeoutError) {
        userError = timeoutError;
      }
      
      const userId = user?.id;
      if (!userId || userError?.message?.includes('timeout')) {
        // Save to localStorage for immediate access
        const existingBrands = SecureStorage.getItem('aicoach-brands', z.array(BrandConfigSchema), []) as BrandConfig[];
        const updatedBrands = existingBrands.filter(b => b.id !== brandWithSlug.id);
        updatedBrands.unshift(brandWithSlug);
        SecureStorage.setItem('aicoach-brands', updatedBrands);
        
        // Also save to database without user_id for public access
        const publicBrandData = {
          id: brandWithSlug.id,
          name: brandWithSlug.name,
          personalized_name: brandWithSlug.personalizedName || null,
          primary_color: brandWithSlug.primaryColor || '#10B981',
          secondary_color: brandWithSlug.secondaryColor || '#06B6D4',
          industry_focus: brandWithSlug.industry || 'General Wellness',
          health_framework: brandWithSlug.framework || 'sense',
          logo_url: brandWithSlug.logoUrl || null,
          avatar_url: brandWithSlug.avatarUrl || null,
          ai_welcome_message: brandWithSlug.aiChatText || null,
          user_example_message: brandWithSlug.userChatText || null,
          ai_follow_up_message: brandWithSlug.aiFollowUpText || null,
          store_url: brandWithSlug.storeUrl || null,
          supplement_protocol: (brandWithSlug.supplementProtocol || []) as any,
          slug: brandWithSlug.slug,
          config: { homeScreenCards: brandWithSlug.homeScreenCards || [] } as any,
          enabled_modules: {
            supplements: false,
            aiCoach: false,
            healthData: false,
            hotFlashTracking: false,
            store: false,
            medicationTracking: false
          },
          ai_provider: brandWithSlug.aiProvider || 'openai_assistant',
          openai_assistant_id: brandWithSlug.openaiAssistantId || null,
          lovable_ai_system_prompt: brandWithSlug.lovableAiSystemPrompt || null,
          store_config: {
            enabled: false,
            integrationType: 'none',
            showPricing: true,
            allowPurchase: true,
            storePageTitle: 'Shop',
            storePageDescription: null
          },
          user_id: null, // Public demo brand
          organization_id: null,
          is_active: true
        };
        
        try {
          await supabase.from('brands').upsert(publicBrandData);
        } catch (dbError) {
          // Continue with localStorage only
        }
        
        setCurrentBrand(brandWithSlug);
        return true;
      }

      // Authenticated user - save to database with user_id
      const brandData = {
        id: brandWithSlug.id,
        name: brandWithSlug.name,
        personalized_name: brandWithSlug.personalizedName || null,
        primary_color: brandWithSlug.primaryColor || '#10B981',
        secondary_color: brandWithSlug.secondaryColor || '#06B6D4',
        industry_focus: brandWithSlug.industry || 'General Wellness',
        health_framework: brandWithSlug.framework || 'sense',
        logo_url: brandWithSlug.logoUrl || null,
        avatar_url: brandWithSlug.avatarUrl || null,
        ai_welcome_message: brandWithSlug.aiChatText || null,
        user_example_message: brandWithSlug.userChatText || null,
        ai_follow_up_message: brandWithSlug.aiFollowUpText || null,
        store_url: brandWithSlug.storeUrl || null,
        supplement_protocol: (brandWithSlug.supplementProtocol || []) as any,
        slug: brandWithSlug.slug,
        config: { homeScreenCards: brandWithSlug.homeScreenCards || [] } as any,
        enabled_modules: {
          supplements: false,
          aiCoach: false,
          healthData: false,
          hotFlashTracking: false,
          store: false,
          medicationTracking: false
        },
        store_config: {
          enabled: false,
          integrationType: 'none',
          showPricing: true,
          allowPurchase: true,
          storePageTitle: 'Shop',
          storePageDescription: null
        },
        user_id: userId,
        organization_id: null,
        is_active: true
      };

      const { data, error } = await supabase
        .from('brands')
        .upsert(brandData)
        .select();

      if (error) {
        console.error('Database error:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      setCurrentBrand(brandWithSlug);
      return true;
    } catch (error) {
      console.error('Error saving brand:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      alert(`Failed to save brand: ${errorMessage}`);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const loadBrand = async (identifier: string): Promise<BrandConfig | null> => {
    if (!identifier || identifier === ':brandId') {
      return null;
    }
    
    // Check localStorage for demo brands first
    try {
      const existingBrands = SecureStorage.getItem('aicoach-brands', z.array(BrandConfigSchema), []) as BrandConfig[];
      const localBrand = existingBrands.find(b => 
        b.id === identifier || 
        b.slug === identifier || 
        b.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') === identifier
      );
      
      if (localBrand) {
        // Ensure slug is set
        if (!localBrand.slug) {
          localBrand.slug = localBrand.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        }
        return localBrand;
      }
    } catch (error) {
      // Continue to database lookup
    }

    // Query database for brand
    try {
      
      let query = supabase
        .from('brands')
        .select(`
          id, name, personalized_name, primary_color, secondary_color,
          industry_focus, health_framework, logo_url, avatar_url,
          ai_welcome_message, user_example_message, ai_follow_up_message,
          store_url, supplement_protocol, created_at, config, slug,
          openai_assistant_id, enabled_modules, module_settings
        `)
        .eq('is_active', true);

      // Search by ID or slug
      if (identifier.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i)) {
        query = query.eq('id', identifier);
      } else {
        query = query.eq('slug', identifier);
      }

      const { data, error } = await query.maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('❌ Database error:', error);
        return null;
      }

      if (data) {
        // Check if data is in config field (newer format) or individual columns (legacy format)
        const configData = data.config as any;
        const hasConfigData = configData && typeof configData === 'object' && configData.name;
        
        let brand: BrandConfig;
        if (hasConfigData) {
          // Use data from config field (newer format)
          brand = {
            id: data.id,
            name: configData.name || data.name,
            personalizedName: configData.personalizedName || data.personalized_name || configData.name,
            primaryColor: configData.primaryColor || data.primary_color || '#10B981',
            secondaryColor: configData.secondaryColor || data.secondary_color || '#06B6D4',
            industry: configData.industry || data.industry_focus || 'wellness',
            framework: configData.framework || (data.health_framework?.includes('SPAN') ? 'span' : 'sense'),
            logoUrl: configData.logoUrl || data.logo_url || '',
            avatarUrl: configData.avatarUrl || data.avatar_url || '',
            aiChatText: configData.aiChatText || data.ai_welcome_message || '',
            userChatText: configData.userChatText || data.user_example_message || '',
            aiFollowUpText: configData.aiFollowUpText || data.ai_follow_up_message || '',
            storeUrl: configData.storeUrl || data.store_url || '',
            supplementProtocol: configData.supplementProtocol || (data.supplement_protocol as any) || [],
            homeScreenCards: configData.homeScreenCards || [],
            createdAt: configData.createdAt || data.created_at,
            slug: data.slug || (configData.name || data.name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
            openaiAssistantId: undefined, // Fetched securely when needed
            enabledModules: data.enabled_modules as any,
            moduleSettings: data.module_settings as any
          };
        } else {
          // Use data from individual columns (legacy format)
          const brandData = data as any; // Type assertion for new fields not yet in generated types
          brand = {
            id: brandData.id,
            name: brandData.name,
            personalizedName: brandData.personalized_name || brandData.name,
            primaryColor: brandData.primary_color || '#10B981',
            secondaryColor: brandData.secondary_color || '#06B6D4',
            industry: brandData.industry_focus || 'General Wellness',
            framework: (brandData.health_framework as 'sense' | 'span') || 'sense',
            logoUrl: brandData.logo_url || '',
            avatarUrl: brandData.avatar_url || '',
            aiChatText: brandData.ai_welcome_message || '',
            userChatText: brandData.user_example_message || '',
            aiFollowUpText: brandData.ai_follow_up_message || '',
            storeUrl: brandData.store_url || '',
            supplementProtocol: (brandData.supplement_protocol as any) || [],
            homeScreenCards: [],
            createdAt: brandData.created_at,
            slug: brandData.slug || brandData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
            aiProvider: (brandData.ai_provider as 'openai_assistant' | 'lovable_ai') || 'openai_assistant',
            openaiAssistantId: undefined, // Fetched securely when needed
            lovableAiSystemPrompt: undefined, // Fetched securely when needed
            enabledModules: brandData.enabled_modules as any,
            moduleSettings: brandData.module_settings as any
          };
        }
        
        return brand;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };

  const getAllBrands = async (): Promise<BrandConfig[]> => {
    try {
      // Get demo brands from local storage first
      const localBrands = SecureStorage.getItem('aicoach-brands', z.array(BrandConfigSchema), []) as BrandConfig[];
      
      // Add timeout for getUser call to prevent hanging
      const getUserWithTimeout = () => {
        return Promise.race([
          supabase.auth.getUser(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('GetUser timeout')), 3000)
          )
        ]);
      };
      
      let user, authError;
      try {
        const result = await getUserWithTimeout() as any;
        user = result.data?.user;
        authError = result.error;
      } catch (timeoutError) {
        authError = timeoutError;
      }
      
      // Always try to load database brands for authenticated users
      if (!user) {
        const localBrands = SecureStorage.getItem('aicoach-brands', z.array(BrandConfigSchema), []) as BrandConfig[];
        return localBrands;
      }

      // Fetch database brands
      
      // Check user role from user_profiles table for proper admin detection
      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('role, brand_id')
        .eq('id', user.id)
        .maybeSingle();

      const userRole = profileData?.role as string;
      const isAdmin = userRole === 'super_admin' || userRole === 'brand_admin' || userRole === 'org_admin';
      
      console.log('🔍 getAllBrands - User role:', userRole, 'isAdmin:', isAdmin);
      
      let query = supabase
        .from('brands')
        .select(`
          id, name, personalized_name, primary_color, secondary_color,
          industry_focus, health_framework, logo_url, avatar_url,
          ai_welcome_message, user_example_message, ai_follow_up_message,
          store_url, supplement_protocol, created_at, config, slug, user_id
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      // Apply filters based on role
      if (!isAdmin) {
        // Regular users see brands they're assigned to
        if (profileData?.brand_id) {
          query = query.eq('id', profileData.brand_id);
        } else {
          // Fallback: check user_id for legacy brands
          query = query.eq('user_id', user.id);
        }
      }
      // Admin users see ALL brands (no filter applied)

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching brands:', error);
        // For new users, this is expected - just return current brand if available
        return currentBrand ? [currentBrand] : [];
      }

      const brands = data?.map(item => {
        // Check if data is in config field (newer format) or individual columns (legacy format)
        const configData = item.config as any;
        const hasConfigData = configData && typeof configData === 'object' && configData.name;
        
        if (hasConfigData) {
          // Use data from config field (newer format)
          return {
            id: item.id,
            name: configData.name || item.name,
            personalizedName: configData.personalizedName || item.personalized_name || configData.name,
            primaryColor: configData.primaryColor || item.primary_color || '#10B981',
            secondaryColor: configData.secondaryColor || item.secondary_color || '#06B6D4',
            industry: configData.industry || item.industry_focus || 'wellness',
            framework: configData.framework || (item.health_framework?.includes('SPAN') ? 'span' : 'sense'),
            logoUrl: configData.logoUrl || item.logo_url || '',
            avatarUrl: configData.avatarUrl || item.avatar_url || '',
            aiChatText: configData.aiChatText || item.ai_welcome_message || '',
            userChatText: configData.userChatText || item.user_example_message || '',
            aiFollowUpText: configData.aiFollowUpText || item.ai_follow_up_message || '',
            storeUrl: configData.storeUrl || item.store_url || '',
            supplementProtocol: configData.supplementProtocol || (item.supplement_protocol as any) || [],
            homeScreenCards: configData.homeScreenCards || [],
            createdAt: configData.createdAt || item.created_at,
            slug: item.slug || (configData.name || item.name).toLowerCase().replace(/[^a-z0-9]+/g, '-')
          } as BrandConfig;
        } else {
          // Use data from individual columns (legacy format)
          return {
            id: item.id,
            name: item.name,
            personalizedName: item.personalized_name || item.name,
            primaryColor: item.primary_color || '#10B981',
            secondaryColor: item.secondary_color || '#06B6D4',
            industry: item.industry_focus || 'General Wellness',
            framework: (item.health_framework as 'sense' | 'span') || 'sense',
            logoUrl: item.logo_url || '',
            avatarUrl: item.avatar_url || '',
            aiChatText: item.ai_welcome_message || '',
            userChatText: item.user_example_message || '',
            aiFollowUpText: item.ai_follow_up_message || '',
            storeUrl: item.store_url || '',
            supplementProtocol: (item.supplement_protocol as any) || [],
            homeScreenCards: [],
            createdAt: item.created_at,
            slug: item.slug || item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')
          } as BrandConfig;
        }
      }) || [];
      
      // Merge with local demo brands and deduplicate
      const allBrands = [...brands];
      
      // For admin users, also include local demo brands
      if (isAdmin) {
        localBrands.forEach(localBrand => {
          if (!allBrands.find(b => b.id === localBrand.id)) {
            allBrands.push(localBrand);
          }
        });
      }
      
      // Include current brand if it's not in any results
      if (currentBrand && !allBrands.find(b => b.id === currentBrand.id)) {
        allBrands.unshift(currentBrand);
      }
      
      // Sort by creation date (newest first)
      const sortedBrands = allBrands.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      console.log('Final merged brands returned:', sortedBrands.length, 'isAdmin:', isAdmin);
      return sortedBrands;
    } catch (error) {
      console.error('Error in getAllBrands:', error);
      // Return local brands as fallback
      const localBrands = SecureStorage.getItem('aicoach-brands', z.array(BrandConfigSchema), []) as BrandConfig[];
      const allLocalBrands = [...localBrands];
      if (currentBrand && !allLocalBrands.find(b => b.id === currentBrand.id)) {
        allLocalBrands.unshift(currentBrand);
      }
      return allLocalBrands;
    }
  };

  const deleteBrand = async (id: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Delete from database first
      const { error } = await supabase
        .from('brands')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting brand from database:', error);
        // Don't return false here - the brand might only exist in localStorage
      }

      // Also remove from localStorage if it exists there
      try {
        const localBrands = SecureStorage.getItem('aicoach-brands', z.array(BrandConfigSchema), []) as BrandConfig[];
        const filteredBrands = localBrands.filter(b => b.id !== id);
        if (filteredBrands.length !== localBrands.length) {
          SecureStorage.setItem('aicoach-brands', filteredBrands);
          console.log('Brand removed from localStorage');
        }
      } catch (localError) {
        console.error('Error removing brand from localStorage:', localError);
      }

      // Clear current brand if it's the one being deleted
      if (currentBrand?.id === id) {
        setCurrentBrand(null);
      }

      console.log('Brand deleted successfully');
      toast.success('Brand deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting brand:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const setBrand = (brand: BrandConfig) => {
    setCurrentBrand(brand);
  };

  return (
    <BrandContext.Provider value={{
      currentBrand,
      setBrand,
      saveBrand,
      loadBrand,
      getAllBrands,
      deleteBrand,
      isLoading
    }}>
      {children}
    </BrandContext.Provider>
  );
};
